import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check, Clock, Loader2, ArrowLeft, Star, Minus, Plus, Hourglass } from "lucide-react";
import LogoType from "@/components/LogoType";
import PromoCountdown from "@/components/PromoCountdown";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [buying, setBuying] = useState(null);
  const [qtys, setQtys] = useState({});

  useEffect(() => {
    document.title = "Book a Tarot Reading — Pricing | Fairy Yasha";
    axios.get(`${API}/reading-tiers`).then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  const getQty = (t) => qtys[t.price_id] || 1;
  const changeQty = (id, delta) =>
    setQtys((q) => ({ ...q, [id]: Math.max(1, Math.min((q[id] || 1) + delta, 20)) }));

  const reserve = async (price_id, quantity = 1) => {
    setBuying(price_id);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        price_id,
        quantity,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#9C2A52]/10 blur-[150px] -z-10" />

      <header className="mx-auto max-w-[1300px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/"><LogoType size="sm" /></Link>
        <Link to="/" data-testid="pricing-back" className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Home
        </Link>
      </header>

      <div className="mx-auto max-w-[1300px] px-6 lg:px-10 pt-10 pb-28" data-testid="pricing-page">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="flex flex-col items-center text-center mb-16 lg:mb-24"
        >
          <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A24B] mb-4">✦ Book a Reading ✦</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-[#EFE3CC] leading-[0.95]">
            Choose your <span className="italic text-gold-gradient">reading</span>
          </h1>
          <p className="mt-6 text-sm sm:text-base text-[#B7A488] font-light max-w-lg">
            Reserve your session below. Once your reading is confirmed, you'll unlock the calendar to choose your date & time.
          </p>
        </motion.div>

        {[
          { id: "promo", title: "Halloween Special", note: "Here for the spooky season only", match: (t) => t.promo },
          { id: "quick", title: "Quick Answers", note: "Yes/no clarity from the pendulum", match: (t) => t.quantity_pricing },
          { id: "chats", title: "Psychic Chat Readings", note: "Live one-on-one chat sessions", match: (t) => !t.promo && !t.quantity_pricing && !t.lookup_key.startsWith("mediumship") },
          { id: "mediumship", title: "Mediumship", note: "Connect with loved ones in spirit", match: (t) => t.lookup_key.startsWith("mediumship") },
        ].map((g) => {
          const list = tiers.filter(g.match);
          if (!list.length) return null;
          const gridCls =
            list.length >= 4
              ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
              : list.length === 1
              ? "grid grid-cols-1 gap-6 max-w-md mx-auto"
              : "grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto";
          return (
            <section key={g.id} className="mb-16 lg:mb-20" data-testid={`tier-group-${g.id}`}>
              <div className="flex items-center gap-5 mb-8 lg:mb-10">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#C9A24B]/30" />
                <div className="text-center">
                  <h2 className="font-display text-base md:text-lg tracking-[0.2em] uppercase text-[#EFE3CC]">
                    ✦ {g.title} ✦
                  </h2>
                  <p className="mt-1 text-xs text-[#B7A488] font-light italic">{g.note}</p>
                </div>
                <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#C9A24B]/30" />
              </div>
              <div className={gridCls}>
                {list.map((t, i) => (
            <motion.div
              key={t.price_id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: i * 0.1, ease }}
              className={`relative flex flex-col rounded-sm p-7 lg:p-8 transition-all duration-500 ${
                t.featured
                  ? "gold-frame bg-gradient-to-b from-[#251320] to-[#1a0f10] shadow-[0_0_50px_rgba(156,42,82,0.22)]"
                  : "border border-[#C9A24B]/18 bg-[#1F130E] hover:border-[#C9A24B]/40"
              }`}
              data-testid={`tier-${t.lookup_key || t.price_id}`}
            >
              {t.promo && (
                <span className="absolute top-5 left-5 flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-[#C77E8A] bg-[#9C2A52]/20 border border-[#C77E8A]/40 rounded-full px-3 py-1" data-testid={`promo-badge-${t.lookup_key}`}>
                  {t.promo_badge || "Limited offer"}
                </span>
              )}
              {t.featured && !t.promo && (
                <span className="absolute top-5 right-5 flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#C9A24B]">
                  <Star className="w-3 h-3" fill="#C9A24B" strokeWidth={0} /> Most Loved
                </span>
              )}
              <h3 className={`font-display text-3xl tracking-tight text-[#EFE3CC] ${t.promo ? "mt-6" : ""}`}>{t.name}</h3>
              <p className="mt-1 text-sm text-[#B7A488] font-light italic line-clamp-3">{t.tagline}</p>

              {t.quantity_pricing ? (
                <>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-display text-5xl text-gold-gradient" data-testid={`tier-total-${t.lookup_key}`}>
                      ${(t.price * getQty(t)).toFixed(0)}
                    </span>
                  </div>
                  <span className="mt-1 text-xs text-[#B7A488]">
                    ${t.price.toFixed(0)} per {t.unit_label || "item"}
                  </span>
                  <div className="mt-4 flex items-center gap-3" data-testid={`qty-picker-${t.lookup_key}`}>
                    <button
                      type="button"
                      onClick={() => changeQty(t.price_id, -1)}
                      data-testid={`qty-dec-${t.lookup_key}`}
                      aria-label="Decrease questions"
                      className="w-9 h-9 rounded-full border border-[#C9A24B]/40 flex items-center justify-center text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all"
                    >
                      <Minus className="w-4 h-4" strokeWidth={1.8} />
                    </button>
                    <span data-testid={`qty-value-${t.lookup_key}`} className="w-8 text-center font-display text-2xl text-[#EFE3CC]">
                      {getQty(t)}
                    </span>
                    <button
                      type="button"
                      onClick={() => changeQty(t.price_id, 1)}
                      data-testid={`qty-inc-${t.lookup_key}`}
                      aria-label="Increase questions"
                      className="w-9 h-9 rounded-full border border-[#C9A24B]/40 flex items-center justify-center text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all"
                    >
                      <Plus className="w-4 h-4" strokeWidth={1.8} />
                    </button>
                    <span className="text-xs text-[#B7A488] ml-1">
                      question{getQty(t) > 1 ? "s" : ""}
                    </span>
                  </div>
                </>
              ) : (
                <div className="mt-5 flex items-baseline gap-3">
                  <span className="font-display text-5xl text-gold-gradient">${t.price.toFixed(0)}</span>
                  {t.original_price ? (
                    <span className="text-xl text-[#8D7C63] line-through" data-testid={`orig-price-${t.lookup_key}`}>${t.original_price.toFixed(0)}</span>
                  ) : null}
                </div>
              )}
              {t.duration ? (
                <span className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#B7A488]">
                  <Clock className="w-3.5 h-3.5 text-[#C9A24B]" strokeWidth={1.5} /> {t.duration}
                </span>
              ) : null}
              {t.promo && t.expires_at ? (
                <div className="mt-3 inline-flex items-center gap-2" data-testid="promo-countdown-wrap">
                  <Hourglass className="w-3.5 h-3.5 text-[#C77E8A]" strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.16em] text-[#C77E8A]">Ends in</span>
                  <PromoCountdown expiresAt={t.expires_at} className="font-display text-base text-[#E9D3A0]" />
                </div>
              ) : null}

              {t.features && t.features.length > 0 ? (
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#B7A488]">
                      <Check className="w-4 h-4 text-[#C77E8A] mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex-1" />
              )}

              <button
                onClick={() => reserve(t.price_id, getQty(t))}
                disabled={buying === t.price_id}
                data-testid={`tier-reserve-${t.lookup_key || t.price_id}`}
                className={`mt-8 rounded-full py-3.5 text-xs tracking-[0.2em] uppercase transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 ${
                  t.featured
                    ? "bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] text-[#EFE3CC] hover:scale-[1.03]"
                    : "border border-[#C9A24B]/40 text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10"
                }`}
              >
                {buying === t.price_id ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Reserve & Pay
              </button>
            </motion.div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <WhatsAppButton />
    </div>
  );
}
